import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { FormattedAddress, SummaryGridItem, SummarySection } from '@css-xp/base';
import SubscriptionDetailsIcon from '@css-xp/base/assets/img/icon-subscription-details.svg';
import LockIcon from '@css-xp/base/assets/img/icon-lock.svg';
import { SoldToAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { NoData, EditSoldToAddressDisabledMessage } from '@css-xp/ppc-common';
import { LayoutAppearance } from '@css-xp/ppc-types';
import { COMMERCIAL_PRICE_ELIGIBILITY } from '@css-xp/ppc-constants';
import { TAX_UTILS } from '@css-xp/ppc-utils';
import './customer-address-summary-section.scss';

interface CustomerAddressSummarySectionProps {
  soldToAddress: SoldToAddress;
  layoutAppearance?: LayoutAppearance;
}

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: ${token('border.radius', '4px')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box;
  padding: ${token('space.100', '8px')} ${token('space.150', '12px')};
  max-width: 330px;
  vertical-align: middle;
`;

const ToolTipContainer = styled.span`
  vertical-align: middle;
`;
export const CustomerAddressSummarySection = ({
  soldToAddress,
  layoutAppearance = LayoutAppearance.DEFAULT,
}: CustomerAddressSummarySectionProps) => {
  const taxLabelInformation = TAX_UTILS.getTaxLabelInformationFromAddress(soldToAddress);
  const isOneColumnLayout = layoutAppearance === LayoutAppearance.ONE_COLUMN;

  const getTaxInformationSection = useCallback(() => {
    const taxInformation = TAX_UTILS.getTaxInformationFromAddress(soldToAddress);
    if (taxInformation.length > 0) {
      return taxInformation.map((tax, idx) => (
        <SummaryGridItem
          key={`tax-id-item-${idx}`}
          className="section-item-internal summary-grid-item-content tax-component-section"
        >
          <label data-testid={`tax-id-label-${idx}`} key={`tax-id-label-${idx}`}>
            {tax.label}
          </label>
          <p data-testid={`tax-id-value-${idx}`} key={`tax-id-value-${idx}`}>
            {tax.value ?? <NoData />}
          </p>
        </SummaryGridItem>
      ));
    }
    return (
      <SummaryGridItem
        key={`tax-id-item`}
        className="section-item-internal summary-grid-item-content tax-component-section"
      >
        <label data-testid={`tax-id-label`} key={`tax-id-label`}>
          Tax ID
        </label>
        <p data-testid={`tax-id-value`} key={`tax-id-value`}>
          <NoData />
        </p>
      </SummaryGridItem>
    );
  }, [soldToAddress]);

  return (
    <SummarySection
      columns="4fr"
      className={classNames('customer-address-summary', { 'customer-address-summary-one-column': isOneColumnLayout })}
    >
      <SummaryGridItem className="summary-grid-item-internal">
        <SummarySection className="summary-section-internal" columns={isOneColumnLayout ? '1fr' : '1fr 1fr'}>
          <SummaryGridItem className="section-item-internal summary-section-item">
            {isOneColumnLayout || (
              <SummaryGridItem className="section-item-internal summary-grid-item-content">
                <div className="section-heading">
                  {/* @ts-ignore */}
                  <img src={SubscriptionDetailsIcon} height={'32px'} alt="heading-icon" />
                  <div className="title">Customer details</div>
                </div>
              </SummaryGridItem>
            )}
            <SummaryGridItem className="section-item-internal summary-grid-item-content">
              <label>Pricing eligibility</label>
              <p>{sentenceCase(soldToAddress?.priceEligibility) || '-'} </p>
            </SummaryGridItem>
            <SummaryGridItem className="section-item-internal summary-grid-item-content">
              <label>Sold-to address</label>
              <div className="customer-address-section">
                <span className="line-clamp">
                  {soldToAddress?.addressLine1 ? (
                    <div>
                      <FormattedAddress
                        addressLine1={soldToAddress?.addressLine1}
                        addressLine2={soldToAddress?.addressLine2}
                        city={soldToAddress?.city}
                        country={soldToAddress?.country}
                        postcode={soldToAddress?.postcode}
                        state={soldToAddress?.state}
                        icon={
                          soldToAddress?.priceEligibility?.toLowerCase() !==
                            COMMERCIAL_PRICE_ELIGIBILITY.toLowerCase() && (
                            <ToolTipContainer>
                              <Tooltip
                                testId="sold-to-address-edit-disabled-tooltip"
                                component={InlineDialog}
                                content={<EditSoldToAddressDisabledMessage />}
                                tag="span"
                              >
                                {/* @ts-ignore */}
                                <img src={LockIcon} data-testid="lock-icon" />
                              </Tooltip>
                            </ToolTipContainer>
                          )
                        }
                        hasLineBreak={!isOneColumnLayout}
                      />
                    </div>
                  ) : (
                    <p>Same as billing address</p>
                  )}
                </span>
              </div>
            </SummaryGridItem>
            {getTaxInformationSection()}
            <SummaryGridItem className="section-item-internal summary-grid-item-content">
              <label>Tax exempt</label>
              <p>
                {taxLabelInformation.map((tax, idx) => (
                  <div key={`tax-exempt-${idx}`}>
                    {tax.taxDisplayName ? `${tax.taxDisplayName} (${tax.taxExempt})` : tax.taxExempt}
                  </div>
                ))}
              </p>
            </SummaryGridItem>
          </SummaryGridItem>
        </SummarySection>
      </SummaryGridItem>
    </SummarySection>
  );
};
